import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';

import { userApi } from 'src/Services/API';
import { UpdateUserConsentsDto, UserWithPrivateInfoDto } from 'src/Services/API/generated';

interface MutationContext {
  previousUserData: UserWithPrivateInfoDto | undefined;
}

export const useUpdateUserConsents = (
  options?: UseMutationOptions<
    UserWithPrivateInfoDto,
    unknown,
    UpdateUserConsentsDto,
    MutationContext | undefined
  >
) => {
  const queryClient = useQueryClient();

  return useMutation<
    UserWithPrivateInfoDto,
    unknown,
    UpdateUserConsentsDto,
    MutationContext | undefined
  >(
    'update-notification-preferences',
    async data => {
      const response = await userApi.userControllerUpdateConsents(data);
      return response.data;
    },
    {
      onMutate: async (
        newPreferences: UpdateUserConsentsDto
      ): Promise<MutationContext | undefined> => {
        await queryClient.cancelQueries('fetch-user');

        const previousUserData = queryClient.getQueryData<UserWithPrivateInfoDto>('fetch-user');

        if (previousUserData) {
          const updatedUserData: UserWithPrivateInfoDto = {
            ...previousUserData,
            ...newPreferences,
          };
          queryClient.setQueryData<UserWithPrivateInfoDto>('fetch-user', updatedUserData);
        }

        return { previousUserData };
      },
      onError: (err, variables, context) => {
        if (context?.previousUserData) {
          queryClient.setQueryData('fetch-user', context.previousUserData);
        }
        options?.onError?.(err, variables, context);
      },
      onSettled: (data, error, variables, context) => {
        queryClient.invalidateQueries('fetch-user');
        options?.onSettled?.(data, error, variables, context);
      },
      ...options,
    }
  );
};
